import { produce } from 'immer';
import { DateTime } from 'luxon';

import type { CREDIT_PROFILE_REDUCER_KEY } from 'app-components/flux/credit-profile/types';
import createForm, { ValidationTrigger } from 'app-components/utils/createForm/createForm';
import type RootState from 'app-components/utils/redux-store/rootState';

import { checkNumericValue, getHyphenatedSSNValue } from './utils';

type State = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  ssn?: string;
};

export type FieldValues = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  ssn?: string;
};

// @ts-expect-error FIXME TS7006
const reducer = (state, action) =>
  // @ts-expect-error FIXME TS7006
  produce(state, (draft) => {
    switch (action.t) {
      case 'valueChange':
        switch (action.field) {
          case 'firstName':
            draft.firstName = action.value;
            break;
          case 'lastName':
            draft.lastName = action.value;
            break;
          case 'phoneNumber':
            if (action.value.startsWith('0')) {
              draft.phoneNumber = action.value.slice(1);
            } else {
              draft.phoneNumber = checkNumericValue(action.value, 10);
            }
            break;
          case 'dateOfBirth':
            draft.dateOfBirth = action.value;
            break;
          case 'addressLine1':
            draft.addressLine1 = action.value;
            break;
          case 'addressLine2':
            draft.addressLine2 = action.value;
            break;
          case 'city':
            draft.city = action.value;
            break;
          case 'state':
            draft.state = action.value;
            break;
          case 'zipCode':
            draft.zipCode = checkNumericValue(action.value, 5);
            break;
          case 'ssn':
            draft.ssn = getHyphenatedSSNValue(action.value);
            break;
        }
    }
  });

const init = (input: State) => input;

const creditProfileForm = createForm({
  types: {} as {
    fieldValues: FieldValues;
    reduxStore: Pick<RootState, typeof CREDIT_PROFILE_REDUCER_KEY>;
  },
  reducer,
  init,
  fieldConfigs: {
    firstName: {
      selector: ({ firstName }) => firstName,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ firstName }) => (!firstName ? 'First name is required' : undefined),
        },
      },
    },
    lastName: {
      selector: ({ lastName }) => lastName,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ lastName }) => (!lastName ? 'Last name is required' : undefined),
        },
      },
    },
    phoneNumber: {
      selector: ({ phoneNumber }) => phoneNumber,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ phoneNumber }) => (!phoneNumber ? 'Phone number is required' : undefined),
        },
      },
    },
    dateOfBirth: {
      selector: ({ dateOfBirth }) => dateOfBirth,
      validation: {
        isValidDate: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ dateOfBirth }) => {
            const objDateOfBirth = DateTime.fromISO(dateOfBirth);
            return !objDateOfBirth.isValid ? 'Invalid date' : undefined;
          },
        },
        isFutureDate: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ dateOfBirth }) => {
            const objDateOfBirth = DateTime.fromISO(dateOfBirth);
            return objDateOfBirth > DateTime.now() ? 'Invalid date of birth' : undefined;
          },
        },
      },
    },
    addressLine1: {
      selector: ({ addressLine1 }) => addressLine1,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ addressLine1 }) => (!addressLine1 ? 'Address is required' : undefined),
        },
      },
    },
    addressLine2: {
      selector: ({ addressLine2 }) => addressLine2,
    },
    city: {
      selector: ({ city }) => city,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ city }) => (!city ? 'City is required' : undefined),
        },
      },
    },
    state: {
      selector: ({ state }) => state,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ state }) => (!state ? 'State is required' : undefined),
        },
      },
    },
    zipCode: {
      selector: ({ zipCode }) => zipCode,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ zipCode }) => (!zipCode ? 'ZIP Code is required' : undefined),
        },
        usOnly: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ zipCode }) => (/^[0-9]{5}$/.test(zipCode) ? undefined : 'Valid only for US residents'),
        },
      },
    },
    ssn: {
      selector: ({ ssn }) => ssn,
      validation: {
        required: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ ssn }) => (!ssn ? 'SSN is required' : undefined),
        },
        validFormat: {
          triggers: ValidationTrigger.onSubmit,
          validate: ({ ssn }) => (ssn.replaceAll?.('-', '').length !== 9 ? 'Enter a valid SSN number' : undefined),
        },
      },
    },
  },
  submitTransformer: (state) => state,
});

export const {
  Provider: CreditScoreRegistrationProvider,
  useFormField,
  ...creditProfileFormHooks
} = creditProfileForm.createManagerProvider();
